import type { ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { CoinIcon } from './icons';
import { RewardPill } from './RewardPill';
import { groupIcon } from './group-icons';

export interface GroupCardProps {
  /** Lucide icon name from GROUP_ICONS (unknown names fall back to star). */
  icon: string;
  name: string;
  totalQuests: number;
  completedCount: number;
  /** Threshold position: solves needed for the bonus (1..totalQuests). */
  neededCount: number;
  /** 0 = purely organizational — no bonus row is rendered. */
  bonusBucks: number;
  bonusAwarded?: boolean;
  /** Localized caption, e.g. `Bonus when all 4 are done` / `✓ Bonus unlocked`. */
  bonusCaption?: string;
  /** Accessible name for the gold pill, e.g. t('ec2.a11y.bucksReward', {amount}). */
  bonusLabel?: string;
  /** Accessible progress text, e.g. `3 of 4 quests done`. */
  countLabel: string;
  collapsed?: boolean;
  /** Chevron/header toggle; header is a plain row when omitted. */
  onToggle?: () => void;
  /** Member rows (GroupMemberRow / HiddenCountRow). Hidden while collapsed. */
  children?: ReactNode;
  className?: string;
}

/**
 * Quest-group card, Concept A (docs/07 addendum): Beam icon chip + name +
 * tabular `x/y` header, segmented progress track (one segment per member
 * quest, completed = Beam) with the gold CloudBuck coin sitting at the
 * threshold notch, bonus caption + gold pill, then member rows. Collapsed
 * cards keep header + track so progress stays glanceable. Gold marks exactly
 * one thing: the bonus.
 */
export function GroupCard(props: GroupCardProps) {
  const { collapsed = false, bonusAwarded = false } = props;
  const Icon = groupIcon(props.icon);
  const segments = Array.from({ length: props.totalQuests }, (_, i) => i < props.completedCount);

  const header = (
    <>
      <span
        aria-hidden
        className="flex size-[34px] shrink-0 items-center justify-center rounded-[11px] bg-accent/14 text-accent"
      >
        <Icon size={17} strokeWidth={2} aria-hidden />
      </span>
      <h3 className="min-w-0 flex-1 truncate text-left font-display text-[17px] font-[650] text-fg">
        {props.name}
      </h3>
      <span aria-hidden className="font-display text-base font-bold text-fg tabular-nums">
        {props.completedCount}
        <small className="text-[13px] font-semibold text-fg-muted">/{props.totalQuests}</small>
      </span>
      <span className="sr-only">{props.countLabel}</span>
      {props.onToggle && (
        <ChevronDown
          size={16}
          aria-hidden
          className={`text-fg-muted transition-transform duration-(--duration-slow) ${collapsed ? '-rotate-90' : ''}`}
        />
      )}
    </>
  );

  return (
    <section
      data-testid="ec-group-card"
      data-collapsed={collapsed || undefined}
      data-bonus-awarded={bonusAwarded || undefined}
      className={`rounded-(--radius-card) border border-border bg-surface ${props.className ?? ''}`}
    >
      {props.onToggle ? (
        <button
          type="button"
          onClick={props.onToggle}
          aria-expanded={!collapsed}
          data-testid="ec-group-toggle"
          className="flex w-full items-center gap-2.5 px-4 pt-3.5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        >
          {header}
        </button>
      ) : (
        <div className="flex items-center gap-2.5 px-4 pt-3.5">{header}</div>
      )}

      {/* One segment per member quest; the coin sits at the threshold notch. */}
      <div aria-hidden data-testid="ec-group-track" className="flex items-center gap-1 px-4 pt-3">
        {segments.map((done, i) => (
          <span key={i} className="contents">
            <span
              data-done={done || undefined}
              className={`h-1.5 flex-1 rounded-[3px] ${done ? (bonusAwarded ? 'bg-success' : 'bg-accent') : 'bg-surface-overlay'}`}
            />
            {i + 1 === props.neededCount && (
              <CoinIcon size={14} data-testid="ec-group-coin" className="-mx-px shrink-0" aria-hidden />
            )}
          </span>
        ))}
      </div>

      {props.bonusBucks > 0 ? (
        <div className="flex items-center justify-between gap-2.5 px-4 pt-2 pb-3.5">
          {props.bonusCaption && (
            <p
              data-testid="ec-group-bonus-caption"
              className={`text-[12.5px] font-medium ${bonusAwarded ? 'font-semibold text-success' : 'text-fg-secondary'}`}
            >
              {props.bonusCaption}
            </p>
          )}
          <RewardPill
            amount={props.bonusBucks}
            label={props.bonusLabel ?? `+${props.bonusBucks}`}
            className="px-2 py-0.5 text-[13px]"
          />
        </div>
      ) : (
        <div className="pb-3.5" />
      )}

      {!collapsed && props.children && (
        <div data-testid="ec-group-members" className="border-t border-border">
          {props.children}
        </div>
      )}
    </section>
  );
}
